import { Box, Dialog, Typography, Button } from '@coolblue-development/becky';
import { RoutingProfile } from '../../../../models/RoutingProfiles';
import { AgentAllocation } from '../../../../models/AgentsAllocation';
import { useRoutingProfileModal } from './useRoutingProfileModal';
import { SelectedAgentsList } from './SelectedAgentsList';
import { RoutingProfileSelector } from './RoutingProfileSelector';
import { setDefaultRoutingProfile } from '../../../../services/setDefaultRoutingProfile';
import { useState } from 'react';

interface DefaultRoutingProfileModalProps {
  open: boolean;
  onClose: () => void;
  selectedAgents: AgentAllocation[];
  allRoutingProfiles: RoutingProfile[];
  onSuccess: () => void;
  onError: (error: string) => void;
}

export const DefaultRoutingProfileModal = ({
  open,
  onClose,
  selectedAgents,
  allRoutingProfiles,
  onSuccess,
  onError
}: DefaultRoutingProfileModalProps): JSX.Element => {
  const {
    routingProfiles,
    selectedRoutingProfile,
    setSelectedRoutingProfile,
    isSelectionDisabled,
    resetState,
    isRoutingProfileSameForAllAgents
  } = useRoutingProfileModal(selectedAgents, allRoutingProfiles, open);

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const handleRoutingProfileChange = (value: string): void => {
    setSelectedRoutingProfile(value);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedRoutingProfile || selectedAgents.length === 0) {
      return;
    }

    try {
      setIsLoading(true);

      await setDefaultRoutingProfile({
        userIds: selectedAgents.map(agent => agent.id),
        routingProfileId: selectedRoutingProfile
      });

      onSuccess();
      handleClose();
    } catch (error) {
      onError(`Failed to set default routing profiles: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const hasError = !isRoutingProfileSameForAllAgents;

  const errorMessage =
    'Cannot change default routing profile because selected agents have different default routing profiles.';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="routing-profile-modal-title"
      aria-describedby="routing-profile-modal-description"
      size="large"
    >
      <Box sx={{ padding: '20px 24px' }}>
        <Typography variant="h2" id="routing-profile-modal-title" mb={5}>
          Set default routing profile
        </Typography>

        <SelectedAgentsList selectedAgents={selectedAgents} />

        <RoutingProfileSelector
          selectedRoutingProfile={selectedRoutingProfile}
          onRoutingProfileChange={handleRoutingProfileChange}
          routingProfiles={routingProfiles}
          hasError={hasError}
          isDisabled={isSelectionDisabled || isLoading}
          errorMessage={hasError ? errorMessage : undefined}
        />

        <Box mt={2} display={'flex'} justifyContent={'flex-end'} gap={2}>
          <Button onClick={handleClose} variant="text" sx={{ marginLeft: 2 }} preset="secondary" disabled={isLoading}>
            Cancel
          </Button>

          <Button
            disabled={!selectedRoutingProfile || selectedAgents.length === 0 || isLoading}
            onClick={handleSubmit}
            preset="primary"
            loading={isLoading}
          >
            {isLoading ? 'Updating...' : 'Confirm'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
